import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  VStack,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Tooltip,
  useToast,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Heading,
  FormControl,
  FormLabel,
  FormHelperText,
  Badge,
  Input,
  Collapse,
  Switch,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';

const AdminSettings = () => {
  const { t } = useTranslation();
  const { accessToken: token } = useAuth();
  const toast = useToast();

  // Estados para cada configuración (valores en minutos)
  const [margenSinDisponibilidadDespuesDeReserva, setMargenSinDisponibilidadDespuesDeReserva] = useState(30);
  const [margenSinDisponibilidadAntesDeReserva, setMargenSinDisponibilidadAntesDeReserva] = useState(0);
  const [avisoServiciosSinCubrir, setAvisoServiciosSinCubrir] = useState(240);
  const [periodoCancelacionExpirado, setPeriodoCancelacionExpirado] = useState(120);
  const [recordatorioInicioServicio, setRecordatorioInicioServicio] = useState(60);
  const [cambioACompletadoSinConfirmar, setCambioACompletadoSinConfirmar] = useState(0);
  const [recordatorioDevolucionServicio, setRecordatorioDevolucionServicio] = useState(120);
  const [antelacionMinimaServicioProgramado, setAntelacionMinimaServicioProgramado] = useState(12);
  const [accessTokenExpireMinutes, setAccessTokenExpireMinutes] = useState(360);
  const [sessionExpirationMinutes, setSessionExpirationMinutes] = useState(1440);
  const [developerEmail, setDeveloperEmail] = useState('admin@overtaxi.es');
  const [emailError, setEmailError] = useState('');
  const [anonymousCustomerEmail, setAnonymousCustomerEmail] = useState('htabletcasa@gmail.com');
  const [anonymousEmailError, setAnonymousEmailError] = useState('');
  const [comisionPorNoEstarRegistrado, setComisionPorNoEstarRegistrado] = useState(1.0);
  const [precioMinimoAdjudicacion, setPrecioMinimoAdjudicacion] = useState(0.5);
  const [precioMaximoAdjudicacion, setPrecioMaximoAdjudicacion] = useState(1.0);
  const [precioReserva, setPrecioReserva] = useState(5.0);
  const [premioNuevoConductor, setPremioNuevoConductor] = useState(10);
  const [premioPorNuevoReferal, setPremioPorNuevoReferal] = useState(20);
  const [redsysMerchantCode, setRedsysMerchantCode] = useState('367589983');
  const [merchantCodeError, setMerchantCodeError] = useState('');
  const [redsysTerminal, setRedsysTerminal] = useState('1');
  const [terminalError, setTerminalError] = useState('');
  const [redsysCurrency, setRedsysCurrency] = useState('978');
  const [currencyError, setCurrencyError] = useState('');
  const [redsysTransactionType, setRedsysTransactionType] = useState('0');
  const [transactionTypeError, setTransactionTypeError] = useState('');
  const [redsysBizumMobilenumber, setRedsysBizumMobilenumber] = useState('+34700000000');
  const [redsysProductionUrl, setRedsysProductionUrl] = useState('https://sis.redsys.es:25443/sis/rest/iniciaPeticionREST');
  const [redsysFormUrl, setRedsysFormUrl] = useState('https://sis.redsys.es/sis/realizarPago');
  const [redsysMerchantUrl, setRedsysMerchantUrl] = useState('https://www.overtaxi.es/api/pagos/notificacion/');
  const [redsysUrlOk, setRedsysUrlOk] = useState('https://www.overtaxi.es/cliente/reserva?estado=ok&reserva_id=');
  const [redsysUrlKo, setRedsysUrlKo] = useState('https://www.overtaxi.es/cliente/reserva?estado=ko&reserva_id=');
  const [chatSupportEnabled, setChatSupportEnabled] = useState(true);
  const [chatSupportPubliclyAvailable, setChatSupportPubliclyAvailable] = useState(true);
  const [chatSupportWhatsappEnabled, setChatSupportWhatsappEnabled] = useState(false);
  const [chatSupportTelegramEnabled, setChatSupportTelegramEnabled] = useState(false);
  const [chatSupportPollIntervalSeconds, setChatSupportPollIntervalSeconds] = useState(10);
  const [chatSupportSettingsRefreshIntervalSeconds, setChatSupportSettingsRefreshIntervalSeconds] = useState(30);
  const [chatSupportAdminMessagesPollIntervalSeconds, setChatSupportAdminMessagesPollIntervalSeconds] = useState(12);
  const [chatSupportAdminChannelsPollIntervalSeconds, setChatSupportAdminChannelsPollIntervalSeconds] = useState(30);
  const [chatSupportAdminLogsPollIntervalSeconds, setChatSupportAdminLogsPollIntervalSeconds] = useState(60);
  const [chatSupportAdminTelegramStatusPollIntervalSeconds, setChatSupportAdminTelegramStatusPollIntervalSeconds] = useState(60);
  const [chatSupportDocsPath, setChatSupportDocsPath] = useState('work/Docs del proyecto/eusta/txt/');
  const [chatSupportDocsPathChicho, setChatSupportDocsPathChicho] = useState('work/Docs del proyecto/chicho/txt/');
  const [permitirLoginTiktok, setPermitirLoginTiktok] = useState(false);
  const [permitirLoginGoogle, setPermitirLoginGoogle] = useState(false);
  const [permitirLoginInstagram, setPermitirLoginInstagram] = useState(false);
  const [permitirLoginFacebook, setPermitirLoginFacebook] = useState(false);
  const [habilitarFlightapi, setHabilitarFlightapi] = useState(false);
  const [habilitarAeroapi, setHabilitarAeroapi] = useState(false);
  const [flightapiTimeoutSeconds, setFlightapiTimeoutSeconds] = useState(8);

  // Estados para mostrar tooltips
  const [showTooltipMargenDespues, setShowTooltipMargenDespues] = useState(false);
  const [showTooltipMargenAntes, setShowTooltipMargenAntes] = useState(false);
  const [showTooltip1, setShowTooltip1] = useState(false);
  const [showTooltip2, setShowTooltip2] = useState(false);
  const [showTooltip3, setShowTooltip3] = useState(false);
  const [showTooltip4, setShowTooltip4] = useState(false);
  const [showTooltip5, setShowTooltip5] = useState(false);
  const [showTooltip6, setShowTooltip6] = useState(false);
  const [showTooltip7, setShowTooltip7] = useState(false);
  const [showTooltip8, setShowTooltip8] = useState(false);
  const [showTooltip9, setShowTooltip9] = useState(false);
  const [showTooltipComision, setShowTooltipComision] = useState(false);
  const [showTooltip10, setShowTooltip10] = useState(false);
  const [showTooltipSession, setShowTooltipSession] = useState(false);

  // Estados para controlar el colapso de secciones
  const [isContactoOpen, setIsContactoOpen] = useState(false);
  const [isPagosOpen, setIsPagosOpen] = useState(false);
  const [isRedsysOpen, setIsRedsysOpen] = useState(false);
  const [isCicloVidaOpen, setIsCicloVidaOpen] = useState(false);
  const [isSeguridadOpen, setIsSeguridadOpen] = useState(false);
  const [isVuelosOpen, setIsVuelosOpen] = useState(false);
  const [isChatSoporteOpen, setIsChatSoporteOpen] = useState(false);

  const initialSectionLoadingState = {
    pagos: false,
    cicloVida: false,
    contacto: false,
    redsys: false,
    seguridad: false,
    vuelos: false,
    chatSoporte: false,
  };
  const initialSectionLoadedState = {
    pagos: false,
    cicloVida: false,
    contacto: false,
    redsys: false,
    seguridad: false,
    vuelos: false,
    chatSoporte: false,
  };

  const [sectionLoading, setSectionLoading] = useState(initialSectionLoadingState);
  const [sectionLoaded, setSectionLoaded] = useState(initialSectionLoadedState);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const reservasSettingsEndpoint = '/reservas';
  const chatSettingsEndpoint = '/chat/soporte';
  const isProductionApi = useMemo(() => apiBaseUrl.includes('overtaxi.es'), [apiBaseUrl]);
  const isLoading = useMemo(() => Object.values(sectionLoading).some(Boolean), [sectionLoading]);

  const reservasConfigs = [
    { key: 'margen_sin_disponibilidad_despues_de_reserva', setter: setMargenSinDisponibilidadDespuesDeReserva },
    { key: 'margen_sin_disponibilidad_antes_de_reserva', setter: setMargenSinDisponibilidadAntesDeReserva },
    { key: 'aviso_servicios_sin_cubrir', setter: setAvisoServiciosSinCubrir },
    { key: 'periodo_cancelacion_expirado', setter: setPeriodoCancelacionExpirado },
    { key: 'recordatorio_inicio_servicio', setter: setRecordatorioInicioServicio },
    { key: 'cambio_a_completado_sin_confirmar', setter: setCambioACompletadoSinConfirmar },
    { key: 'recordatorio_devolucion_servicio', setter: setRecordatorioDevolucionServicio },
    { key: 'antelacion_minima_servicio_programado', setter: setAntelacionMinimaServicioProgramado },
    { key: 'access_token_expire_minutes', setter: setAccessTokenExpireMinutes },
    { key: 'session_expiration_minutes', setter: setSessionExpirationMinutes },
    { key: 'developer_email', setter: setDeveloperEmail },
    { key: 'anonymous_customer_email', setter: setAnonymousCustomerEmail },
    { key: 'comision_por_no_estar_registrado', setter: setComisionPorNoEstarRegistrado },
    { key: 'precio_minimo_adjudicacion', setter: setPrecioMinimoAdjudicacion },
    { key: 'precio_maximo_adjudicacion', setter: setPrecioMaximoAdjudicacion },
    { key: 'precio_reserva', setter: setPrecioReserva },
    { key: 'premio_nuevo_conductor', setter: setPremioNuevoConductor },
    { key: 'premio_por_nuevo_referal', setter: setPremioPorNuevoReferal },
    { key: 'redsys_merchant_code', setter: setRedsysMerchantCode },
    { key: 'redsys_terminal', setter: setRedsysTerminal },
    { key: 'redsys_currency', setter: setRedsysCurrency },
    { key: 'redsys_transaction_type', setter: setRedsysTransactionType },
    { key: 'redsys_bizum_mobilenumber', setter: setRedsysBizumMobilenumber },
    { key: 'redsys_prod_url', setter: setRedsysProductionUrl, legacyKey: 'redsys_production_url' },
    { key: 'redsys_prod_form_url', setter: setRedsysFormUrl, legacyKey: 'redsys_form_url' },
    { key: 'redsys_prod_merchanturl', setter: setRedsysMerchantUrl, legacyKey: 'redsys_merchanturl' },
    { key: 'redsys_prod_urlok', setter: setRedsysUrlOk, legacyKey: 'redsys_urlok' },
    { key: 'redsys_prod_urlko', setter: setRedsysUrlKo, legacyKey: 'redsys_urlko' },
    { key: 'permitir_login_tiktok', setter: setPermitirLoginTiktok },
    { key: 'permitir_login_google', setter: setPermitirLoginGoogle },
    { key: 'permitir_login_instagram', setter: setPermitirLoginInstagram },
    { key: 'permitir_login_facebook', setter: setPermitirLoginFacebook },
    { key: 'habilitar_flightapi', setter: setHabilitarFlightapi },
    { key: 'habilitar_aeroapi', setter: setHabilitarAeroapi },
    { key: 'flightapi_timeout_seconds', setter: setFlightapiTimeoutSeconds },
  ];

  const boolKeys = new Set([
    'permitir_login_tiktok',
    'permitir_login_google',
    'permitir_login_instagram',
    'permitir_login_facebook',
    'habilitar_flightapi',
    'habilitar_aeroapi',
  ]);

  const reservasKeysBySection = {
    pagos: [
      'precio_maximo_adjudicacion',
      'precio_minimo_adjudicacion',
      'comision_por_no_estar_registrado',
      'precio_reserva',
      'premio_nuevo_conductor',
      'premio_por_nuevo_referal',
    ],
    cicloVida: [
      'antelacion_minima_servicio_programado',
      'margen_sin_disponibilidad_despues_de_reserva',
      'margen_sin_disponibilidad_antes_de_reserva',
      'aviso_servicios_sin_cubrir',
      'periodo_cancelacion_expirado',
      'recordatorio_inicio_servicio',
      'cambio_a_completado_sin_confirmar',
      'recordatorio_devolucion_servicio',
    ],
    contacto: [
      'developer_email',
      'anonymous_customer_email',
    ],
    redsys: [
      'redsys_merchant_code',
      'redsys_terminal',
      'redsys_currency',
      'redsys_transaction_type',
      'redsys_bizum_mobilenumber',
      'redsys_prod_url',
      'redsys_prod_form_url',
      'redsys_prod_merchanturl',
      'redsys_prod_urlok',
      'redsys_prod_urlko',
    ],
    seguridad: [
      'access_token_expire_minutes',
      'session_expiration_minutes',
      'permitir_login_tiktok',
      'permitir_login_google',
      'permitir_login_instagram',
      'permitir_login_facebook',
    ],
    vuelos: [
      'habilitar_flightapi',
      'habilitar_aeroapi',
      'flightapi_timeout_seconds',
    ],
  };

  const chatSupportConfigs = [
    { key: 'chat_support_enabled', setter: setChatSupportEnabled },
    { key: 'chat_support_publicly_available', setter: setChatSupportPubliclyAvailable },
    { key: 'chat_support_whatsapp_enabled', setter: setChatSupportWhatsappEnabled },
    { key: 'chat_support_telegram_enabled', setter: setChatSupportTelegramEnabled },
    { key: 'chat_support_poll_interval_seconds', setter: setChatSupportPollIntervalSeconds },
    { key: 'chat_support_settings_refresh_interval_seconds', setter: setChatSupportSettingsRefreshIntervalSeconds },
    { key: 'chat_support_admin_messages_poll_interval_seconds', setter: setChatSupportAdminMessagesPollIntervalSeconds },
    { key: 'chat_support_admin_channels_poll_interval_seconds', setter: setChatSupportAdminChannelsPollIntervalSeconds },
    { key: 'chat_support_admin_logs_poll_interval_seconds', setter: setChatSupportAdminLogsPollIntervalSeconds },
    { key: 'chat_support_admin_telegram_status_poll_interval_seconds', setter: setChatSupportAdminTelegramStatusPollIntervalSeconds },
    { key: 'chat_support_docs_path', setter: setChatSupportDocsPath },
    { key: 'chat_support_docs_path_chicho', setter: setChatSupportDocsPathChicho },
  ];

  const chatSupportKeys = chatSupportConfigs.map((item) => item.key);

  // Función helper para convertir minutos a formato legible
  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}min`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}min`;
    }
  };

  const toBooleanValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true;
      if (['false', '0', 'no', 'n', 'off', ''].includes(normalized)) return false;
    }
    return Boolean(value);
  };

  const fetchReservasSettings = async (keysToLoad) => {
    try {
      const targetConfigs = reservasConfigs.filter((config) => keysToLoad.includes(config.key));

      for (const config of targetConfigs) {
        try {
          const response = await fetch(
            `${apiBaseUrl}/api/settings${reservasSettingsEndpoint}/config/${config.key}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            let value = await response.json();
            if ((value === null || value === undefined) && config.legacyKey) {
              const legacyResponse = await fetch(
                `${apiBaseUrl}/api/settings${reservasSettingsEndpoint}/config/${config.legacyKey}`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                }
              );
              if (legacyResponse.ok) {
                value = await legacyResponse.json();
              }
            }

            if (value !== null && value !== undefined) {
              if (boolKeys.has(config.key)) {
                config.setter(toBooleanValue(value));
              } else {
                config.setter(value);
              }
            }
          } else {
            const errorText = await response.text();
            console.error(`[AdminSettings] Error HTTP ${response.status} al cargar ${config.key}:`, errorText);
          }
        } catch (error) {
          console.error(`[AdminSettings] Error loading ${config.key}:`, error);
        }
      }
    } catch (error) {
      console.error('[AdminSettings] Error loading settings:', error);
    }
  };

  const fetchChatSupportSettings = async (keysToLoad = chatSupportKeys) => {
    try {
      const targetConfigs = chatSupportConfigs.filter((item) => keysToLoad.includes(item.key));

      for (const item of targetConfigs) {
        const response = await fetch(
          `${apiBaseUrl}/api/settings${chatSettingsEndpoint}/config/${item.key}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const value = await response.json();
          if (value !== null && value !== undefined) {
            if (['chat_support_docs_path', 'chat_support_docs_path_chicho'].includes(item.key)) {
              item.setter(String(value));
            } else if (item.key === 'chat_support_poll_interval_seconds') {
              const parsed = Number.parseInt(value, 10);
              const clamped = Number.isNaN(parsed) ? 10 : Math.max(8, Math.min(40, parsed));
              item.setter(clamped);
            } else if (item.key === 'chat_support_settings_refresh_interval_seconds') {
              const parsed = Number.parseInt(value, 10);
              const clamped = Number.isNaN(parsed) ? 30 : Math.max(8, Math.min(60, parsed));
              item.setter(clamped);
            } else if (item.key === 'chat_support_admin_messages_poll_interval_seconds') {
              const parsed = Number.parseInt(value, 10);
              const clamped = Number.isNaN(parsed) ? 12 : Math.max(8, Math.min(30, parsed));
              item.setter(clamped);
            } else if (item.key === 'chat_support_admin_channels_poll_interval_seconds') {
              const parsed = Number.parseInt(value, 10);
              const clamped = Number.isNaN(parsed) ? 30 : Math.max(15, Math.min(60, parsed));
              item.setter(clamped);
            } else if (item.key === 'chat_support_admin_logs_poll_interval_seconds') {
              const parsed = Number.parseInt(value, 10);
              const clamped = Number.isNaN(parsed) ? 60 : Math.max(30, Math.min(180, parsed));
              item.setter(clamped);
            } else if (item.key === 'chat_support_admin_telegram_status_poll_interval_seconds') {
              const parsed = Number.parseInt(value, 10);
              const clamped = Number.isNaN(parsed) ? 60 : Math.max(30, Math.min(180, parsed));
              item.setter(clamped);
            } else {
              item.setter(toBooleanValue(value));
            }
          }
        }
      }
    } catch (error) {
      console.error('[AdminSettings] Error loading chat support settings:', error);
    }
  };

  const loadSectionSettings = async (sectionName) => {
    if (!token || sectionLoaded[sectionName] || sectionLoading[sectionName]) {
      return;
    }

    setSectionLoading((prev) => ({ ...prev, [sectionName]: true }));
    try {
      if (sectionName === 'chatSoporte') {
        await fetchChatSupportSettings(chatSupportKeys);
      } else {
        const keys = reservasKeysBySection[sectionName] || [];
        await fetchReservasSettings(keys);
      }
      setSectionLoaded((prev) => ({ ...prev, [sectionName]: true }));
    } finally {
      setSectionLoading((prev) => ({ ...prev, [sectionName]: false }));
    }
  };

  const handleSectionToggle = (sectionName, isOpen, setOpen) => {
    const nextOpenState = !isOpen;
    setOpen(nextOpenState);
    if (nextOpenState) {
      loadSectionSettings(sectionName);
    }
  };

  // Función para guardar automáticamente una configuración individual
  const autoSaveSetting = async (key, value, description) => {
    console.log(`[AdminSettings] Guardando ${key} con valor ${value}`);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${reservasSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: key,
            value: value,
            type: 'int',
            description: description,
          }),
        }
      );

      console.log(`[AdminSettings] Response status para ${key}:`, response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`[AdminSettings] Guardado exitoso para ${key}:`, data);
        console.log(`[AdminSettings] Guardado exitoso para ${key}:`, data);
        toast({
          title: t('admin_settings_toast_guardado'),
          description: `${description.substring(0, 60)}...`,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        const errorData = await response.text();
        console.error(`[AdminSettings] Error al guardar ${key}:`, response.status, errorData);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const autoSaveChatSetting = async (key, value, description) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${chatSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key,
            value,
            type: 'bool',
            description,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AdminSettings] Error saving chat support setting:', response.status, errorText);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        toast({
          title: t('admin_settings_toast_guardado'),
          description: description,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error('[AdminSettings] Error saving chat support setting:', error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const autoSaveChatStringSetting = async (key, value, description) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${chatSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key,
            value,
            type: 'string',
            description,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AdminSettings] Error saving chat support string setting:', response.status, errorText);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        toast({
          title: t('admin_settings_toast_guardado'),
          description: description,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error('[AdminSettings] Error saving chat support string setting:', error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const autoSaveChatIntSetting = async (key, value, description) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${chatSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key,
            value,
            type: 'int',
            description,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AdminSettings] Error saving chat support int setting:', response.status, errorText);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        toast({
          title: t('admin_settings_toast_guardado'),
          description,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error('[AdminSettings] Error saving chat support int setting:', error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const autoSaveBoolSetting = async (key, value, description) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${reservasSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: key,
            value: value,
            type: 'bool',
            description: description,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log(`[AdminSettings] Guardado exitoso para ${key}:`, data);
        toast({
          title: t('admin_settings_toast_guardado'),
          description: description,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        const errorData = await response.text();
        console.error(`[AdminSettings] Error al guardar ${key}:`, response.status, errorData);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  // Función para guardar configuraciones de texto (string)
  const autoSaveStringSetting = async (key, value, description) => {
    console.log(`[AdminSettings] Guardando ${key} con valor ${value}`);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${reservasSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: key,
            value: value,
            type: 'string',
            description: description,
          }),
        }
      );

      console.log(`[AdminSettings] Response status para ${key}:`, response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`[AdminSettings] Guardado exitoso para ${key}:`, data);
        toast({
          title: t('admin_settings_toast_guardado'),
          description: description,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
        setEmailError(''); // Limpiar error si el guardado fue exitoso
      } else {
        const errorData = await response.text();
        console.error(`[AdminSettings] Error al guardar ${key}:`, response.status, errorData);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  // Función para validar y guardar el código de comercio de Redsys
  const handleMerchantCodeBlur = async () => {
    // Validar que tenga exactamente 9 caracteres
    if (redsysMerchantCode.length !== 9) {
      setMerchantCodeError(t('admin_settings_merchant_code_invalid'));
      return;
    }
    
    setMerchantCodeError('');
    await autoSaveStringSetting(
      'redsys_merchant_code',
      redsysMerchantCode,
      t('admin_settings_redsys_merchant_code_desc')
    );
  };

  // Función para validar y guardar el terminal de Redsys
  const handleTerminalBlur = async () => {
    // Validar que tenga máximo 2 caracteres
    if (redsysTerminal.length > 2 || redsysTerminal.length === 0) {
      setTerminalError(t('admin_settings_terminal_invalid'));
      return;
    }
    
    setTerminalError('');
    await autoSaveStringSetting(
      'redsys_terminal',
      redsysTerminal,
      t('admin_settings_redsys_terminal_desc')
    );
  };

  // Función para validar y guardar la moneda de Redsys
  const handleCurrencyBlur = async () => {
    // Validar que tenga exactamente 3 caracteres
    if (redsysCurrency.length !== 3) {
      setCurrencyError(t('admin_settings_currency_invalid'));
      return;
    }
    
    setCurrencyError('');
    await autoSaveStringSetting(
      'redsys_currency',
      redsysCurrency,
      t('admin_settings_redsys_currency_desc')
    );
  };

  // Función para validar y guardar el tipo de transacción de Redsys
  const handleTransactionTypeBlur = async () => {
    // Validar que tenga exactamente 1 carácter
    if (redsysTransactionType.length !== 1) {
      setTransactionTypeError(t('admin_settings_transaction_type_invalid'));
      return;
    }
    
    setTransactionTypeError('');
    await autoSaveStringSetting(
      'redsys_transaction_type',
      redsysTransactionType,
      t('admin_settings_redsys_transaction_type_desc')
    );
  };

  // Función para guardar configuraciones decimales (float)
  const autoSaveFloatSetting = async (key, value, description) => {
    console.log(`[AdminSettings] Guardando ${key} con valor ${value}`);
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/settings${reservasSettingsEndpoint}/config`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: key,
            value: value,
            type: 'float',
            description: description,
          }),
        }
      );

      console.log(`[AdminSettings] Response status para ${key}:`, response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`[AdminSettings] Guardado exitoso para ${key}:`, data);
        toast({
          title: t('admin_settings_toast_guardado'),
          description: description,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        const errorData = await response.text();
        console.error(`[AdminSettings] Error al guardar ${key}:`, response.status, errorData);
        toast({
          title: t('admin_settings_toast_error'),
          description: t('admin_settings_toast_error_desc'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error) {
      console.error(`Error saving ${key}:`, error);
      toast({
        title: t('admin_settings_toast_error'),
        description: t('admin_settings_toast_error_conexion'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  // Validación de email
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Manejador para el campo de email
  const handleEmailBlur = () => {
    if (developerEmail.trim() === '') {
      setEmailError(t('admin_settings_email_required'));
      return;
    }
    if (!validateEmail(developerEmail)) {
      setEmailError(t('admin_settings_email_invalid'));
      return;
    }
    setEmailError('');
    autoSaveStringSetting('developer_email', developerEmail, t('admin_settings_developer_email_desc'));
  };

  // Manejador para el email del cliente anónimo
  const handleAnonymousEmailBlur = () => {
    if (anonymousCustomerEmail.trim() === '') {
      setAnonymousEmailError(t('admin_settings_email_required'));
      return;
    }
    if (!validateEmail(anonymousCustomerEmail)) {
      setAnonymousEmailError(t('admin_settings_email_invalid'));
      return;
    }
    setAnonymousEmailError('');
    autoSaveStringSetting('anonymous_customer_email', anonymousCustomerEmail, t('admin_settings_anonymous_customer_email_desc'));
  };

  useEffect(() => {
    setSectionLoading(initialSectionLoadingState);
    setSectionLoaded(initialSectionLoadedState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);


  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" overflow="auto" className="admin-page-container">
      <VStack spacing={6} align="stretch">
        {/* Título principal */}
        <Box display="flex" justifyContent="space-between" alignItems="center" className="admin-page-header">
          <Text fontSize="xl" fontWeight="bold" className="admin-page-title">
            {t('admin_settings_titulo')}
          </Text>
        </Box>

        <Divider />

        {/* Sección: Pagos */}
        <Card bg="green.50" variant="outline" borderColor="gray.700">
          <CardHeader 
            cursor="pointer" 
            onClick={() => handleSectionToggle('pagos', isPagosOpen, setIsPagosOpen)}
            _hover={{ bg: 'green.100' }}
            borderTopRadius="md"
            borderBottomRadius={isPagosOpen ? "none" : "md"}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">{t('admin_settings_seccion_pagos')}</Heading>
              <Text fontSize="2xl">{isPagosOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isPagosOpen} animateOpacity>
            <CardBody>
              {sectionLoading.pagos ? (
                <Text fontWeight="bold" color="gray.600">Cargando...</Text>
              ) : (
              <VStack spacing={8} align="stretch">
                
                {/* Configuración 8: Precio máximo de adjudicación */}
                <FormControl>
                  <FormLabel fontWeight="bold" className="admin-form-label">
                    {t('admin_settings_precio_maximo_adjudicacion')} <Badge colorScheme="green" ml={2}>precio_maximo_adjudicacion</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={0}
                        max={5}
                        step={0.1}
                        value={precioMaximoAdjudicacion}
                        onChange={(val) => setPrecioMaximoAdjudicacion(val)}
                        onChangeEnd={(val) => autoSaveFloatSetting('precio_maximo_adjudicacion', val, t('admin_settings_precio_maximo_adjudicacion'))}
                        onMouseEnter={() => setShowTooltip7(true)}
                        onMouseLeave={() => setShowTooltip7(false)}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={0} mt={2} fontSize="sm">
                          0€
                        </SliderMark>
                        <SliderMark value={1} mt={2} fontSize="sm">
                          1€
                        </SliderMark>
                        <SliderMark value={2.5} mt={2} fontSize="sm">
                          2.5€
                        </SliderMark>
                        <SliderMark value={5} mt={2} ml={-2} fontSize="sm">
                          5€
                        </SliderMark>
                        <SliderTrack bg="green.100">
                          <SliderFilledTrack bg="green.500" />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          bg="green.500"
                          color="white"
                          placement="top"
                          isOpen={showTooltip7}
                          label={`${precioMaximoAdjudicacion.toFixed(1)}€`}
                        >
                          <SliderThumb boxSize={6} />
                        </Tooltip>
                      </Slider>
                    </Box>
                    <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                      {precioMaximoAdjudicacion.toFixed(1)}€
                    </Text>
                  </HStack>
                  <FormHelperText mt={4}>
                    {t('admin_settings_precio_maximo_adjudicacion_desc')}
                  </FormHelperText>
                </FormControl>

                <Divider />

                {/* Configuración 8b: Precio mínimo de adjudicación */}
                <FormControl>
                  <FormLabel fontWeight="bold" className="admin-form-label">
                    {t('admin_settings_precio_minimo_adjudicacion')} <Badge colorScheme="green" ml={2}>precio_minimo_adjudicacion</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={0}
                        max={5}
                        step={0.1}
                        value={precioMinimoAdjudicacion}
                        onChange={(val) => setPrecioMinimoAdjudicacion(val)}
                        onChangeEnd={(val) => autoSaveFloatSetting('precio_minimo_adjudicacion', val, t('admin_settings_precio_minimo_adjudicacion'))}
                        onMouseEnter={() => setShowTooltip8(true)}
                        onMouseLeave={() => setShowTooltip8(false)}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={0} mt={2} fontSize="sm">
                          0€
                        </SliderMark>
                        <SliderMark value={1} mt={2} fontSize="sm">
                          1€
                        </SliderMark>
                        <SliderMark value={2.5} mt={2} fontSize="sm">
                          2.5€
                        </SliderMark>
                        <SliderMark value={5} mt={2} ml={-2} fontSize="sm">
                          5€
                        </SliderMark>
                        <SliderTrack bg="green.100">
                          <SliderFilledTrack bg="green.500" />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          bg="green.500"
                          color="white"
                          placement="top"
                          isOpen={showTooltip8}
                          label={`${precioMinimoAdjudicacion.toFixed(1)}€`}
                        >
                          <SliderThumb boxSize={6} />
                        </Tooltip>
                      </Slider>
                    </Box>
                    <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                      {precioMinimoAdjudicacion.toFixed(1)}€
                    </Text>
                  </HStack>
                  <FormHelperText mt={4}>
                    {t('admin_settings_precio_minimo_adjudicacion_desc')}
                  </FormHelperText>
                </FormControl>

                <Divider />

                {/* Configuración NUEVA: Comisión por no estar registrado */}
                <FormControl>
                  <FormLabel fontWeight="bold" className="admin-form-label">
                    {t('admin_settings_comision_no_registrado')} <Badge colorScheme="orange" ml={2}>comision_por_no_estar_registrado</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={0}
                        max={5}
                        step={0.5}
                        value={comisionPorNoEstarRegistrado}
                        onChange={(val) => setComisionPorNoEstarRegistrado(val)}
                        onChangeEnd={(val) => autoSaveFloatSetting('comision_por_no_estar_registrado', val, t('admin_settings_comision_no_registrado'))}
                        onMouseEnter={() => setShowTooltipComision(true)}
                        onMouseLeave={() => setShowTooltipComision(false)}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={0} mt={2} fontSize="sm">
                          0€
                        </SliderMark>
                        <SliderMark value={2.5} mt={2} fontSize="sm">
                          2.5€
                        </SliderMark>
                        <SliderMark value={5} mt={2} ml={-2} fontSize="sm">
                          5€
                        </SliderMark>
                        <SliderTrack bg="orange.100">
                          <SliderFilledTrack bg="orange.500" />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          bg="orange.500"
                          color="white"
                          placement="top"
                          isOpen={showTooltipComision}
                          label={`${comisionPorNoEstarRegistrado.toFixed(1)}€`}
                        >
                          <SliderThumb boxSize={6} />
                        </Tooltip>
                      </Slider>
                    </Box>
                    <Text minW="80px" textAlign="right" fontWeight="bold" color="orange.600">
                      {comisionPorNoEstarRegistrado.toFixed(1)}€
                    </Text>
                  </HStack>
                  <FormHelperText mt={4}>
                    {t('admin_settings_comision_no_registrado_desc')}
                  </FormHelperText>
                </FormControl>

                <Divider />

                {/* Configuración 9: Precio de reserva */}
                <FormControl>
                  <FormLabel fontWeight="bold" className="admin-form-label">
                    {t('admin_settings_precio_reserva')} <Badge colorScheme="green" ml={2}>precio_reserva</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={1}
                        max={10}
                        step={0.5}
                        value={precioReserva}
                        onChange={(val) => setPrecioReserva(val)}
                        onChangeEnd={(val) => autoSaveFloatSetting('precio_reserva', val, t('admin_settings_precio_reserva'))}
                        onMouseEnter={() => setShowTooltip8(true)}
                        onMouseLeave={() => setShowTooltip8(false)}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={1} mt={2} fontSize="sm">
                          1€
                        </SliderMark>
                        <SliderMark value={3} mt={2} fontSize="sm">
                          3€
                        </SliderMark>
                        <SliderMark value={5} mt={2} fontSize="sm">
                          5€
                        </SliderMark>
                        <SliderMark value={10} mt={2} ml={-2} fontSize="sm">
                          10€
                        </SliderMark>
                        <SliderTrack bg="green.100">
                          <SliderFilledTrack bg="green.500" />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          bg="green.500"
                          color="white"
                          placement="top"
                          isOpen={showTooltip8}
                          label={`${precioReserva.toFixed(1)}€`}
                        >
                          <SliderThumb boxSize={6} />
                        </Tooltip>
                      </Slider>
                    </Box>
                    <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                      {precioReserva.toFixed(1)}€
                    </Text>
                  </HStack>
                  <FormHelperText mt={4}>
                    {t('admin_settings_precio_reserva_desc')}
                  </FormHelperText>
                </FormControl>

                <Divider />

                {/* Configuración: Premio nuevo conductor */}
                <FormControl>
                  <FormLabel fontWeight="bold" className="admin-form-label">
                    {t('admin_settings_premio_nuevo_conductor')} <Badge colorScheme="green" ml={2}>premio_nuevo_conductor</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={0}
                        max={50}
                        step={1}
                        value={premioNuevoConductor}
                        onChange={(val) => setPremioNuevoConductor(val)}
                        onChangeEnd={(val) => autoSaveFloatSetting('premio_nuevo_conductor', val, t('admin_settings_premio_nuevo_conductor'))}
                        onMouseEnter={() => setShowTooltip9(true)}
                        onMouseLeave={() => setShowTooltip9(false)}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={0} mt={2} fontSize="sm">
                          0€
                        </SliderMark>
                        <SliderMark value={10} mt={2} fontSize="sm">
                          10€
                        </SliderMark>
                        <SliderMark value={25} mt={2} fontSize="sm">
                          25€
                        </SliderMark>
                        <SliderMark value={50} mt={2} ml={-2} fontSize="sm">
                          50€
                        </SliderMark>
                        <SliderTrack bg="green.100">
                          <SliderFilledTrack bg="green.500" />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          bg="green.500"
                          color="white"
                          placement="top"
                          isOpen={showTooltip9}
                          label={`${premioNuevoConductor}€`}
                        >
                          <SliderThumb boxSize={6} />
                        </Tooltip>
                      </Slider>
                    </Box>
                    <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                      {premioNuevoConductor}€
                    </Text>
                  </HStack>
                  <FormHelperText mt={4}>
                    {t('admin_settings_premio_nuevo_conductor_desc')}
                  </FormHelperText>
                </FormControl>

                <Divider />

                {/* Configuración: Premio por nuevo referal */}
                <FormControl>
                  <FormLabel fontWeight="bold" className="admin-form-label">
                    {t('admin_settings_premio_por_nuevo_referal')} <Badge colorScheme="green" ml={2}>premio_por_nuevo_referal</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={0}
                        max={50}
                        step={1}
                        value={premioPorNuevoReferal}
                        onChange={(val) => setPremioPorNuevoReferal(val)}
                        onChangeEnd={(val) => autoSaveFloatSetting('premio_por_nuevo_referal', val, t('admin_settings_premio_por_nuevo_referal'))}
                        onMouseEnter={() => setShowTooltip10(true)}
                        onMouseLeave={() => setShowTooltip10(false)}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={0} mt={2} fontSize="sm">
                          0€
                        </SliderMark>
                        <SliderMark value={10} mt={2} fontSize="sm">
                          10€
                        </SliderMark>
                        <SliderMark value={25} mt={2} fontSize="sm">
                          25€
                        </SliderMark>
                        <SliderMark value={50} mt={2} ml={-2} fontSize="sm">
                          50€
                        </SliderMark>
                        <SliderTrack bg="green.100">
                          <SliderFilledTrack bg="green.500" />
                        </SliderTrack>
                        <Tooltip
                          hasArrow
                          bg="green.500"
                          color="white"
                          placement="top"
                          isOpen={showTooltip10}
                          label={`${premioPorNuevoReferal}€`}
                        >
                          <SliderThumb boxSize={6} />
                        </Tooltip>
                      </Slider>
                    </Box>
                    <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                      {premioPorNuevoReferal}€
                    </Text>
                  </HStack>
                  <FormHelperText mt={4}>
                    {t('admin_settings_premio_por_nuevo_referal_desc')}
                  </FormHelperText>
                </FormControl>

              </VStack>
              )}
            </CardBody>
          </Collapse>
        </Card>

        {/* Sección: Ciclo de vida de las reservas */}
        <Card bg="blue.50" variant="outline" borderColor="gray.700">
          <CardHeader 
            cursor="pointer" 
            onClick={() => handleSectionToggle('cicloVida', isCicloVidaOpen, setIsCicloVidaOpen)}
            _hover={{ bg: 'blue.100' }}
            borderTopRadius="md"
            borderBottomRadius={isCicloVidaOpen ? "none" : "md"}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">{t('admin_settings_seccion_ciclo_vida')}</Heading>
              <Text fontSize="2xl">{isCicloVidaOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isCicloVidaOpen} animateOpacity>
            <CardBody>
            {sectionLoading.cicloVida ? (
              <Text fontWeight="bold" color="gray.600">Cargando...</Text>
            ) : (
            <VStack spacing={8} align="stretch">
              
              {/* Configuración: Antelación mínima servicio programado */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_antelacion_minima_servicio_programado')} <Badge colorScheme="cyan" ml={2}>antelacion_minima_servicio_programado</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="antelacion-minima-servicio-programado"
                      min={6}
                      max={24}
                      step={1}
                      value={antelacionMinimaServicioProgramado}
                      onChange={(val) => setAntelacionMinimaServicioProgramado(val)}
                      onChangeEnd={(val) => autoSaveSetting('antelacion_minima_servicio_programado', val, t('admin_settings_antelacion_minima_servicio_programado_desc'))}
                      onMouseEnter={() => setShowTooltip6(true)}
                      onMouseLeave={() => setShowTooltip6(false)}
                    >
                      <SliderMark value={6} mt="2" ml="-2" fontSize="sm">
                        6h
                      </SliderMark>
                      <SliderMark value={15} mt="2" ml="-2" fontSize="sm">
                        15h
                      </SliderMark>
                      <SliderMark value={24} mt="2" ml="-2" fontSize="sm">
                        24h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="cyan.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="cyan.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip6}
                        label={`${antelacionMinimaServicioProgramado}h`}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="cyan.600">
                    {antelacionMinimaServicioProgramado}h
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_antelacion_minima_servicio_programado_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Margen sin disponibilidad después de reserva */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_margen_despues_reserva')} <Badge colorScheme="green" ml={2}>margen_sin_disponibilidad_despues_de_reserva</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="margen-sin-disponibilidad-despues"
                      min={0}
                      max={120}
                      step={1}
                      value={margenSinDisponibilidadDespuesDeReserva}
                      onChange={(val) => setMargenSinDisponibilidadDespuesDeReserva(val)}
                      onChangeEnd={(val) => autoSaveSetting('margen_sin_disponibilidad_despues_de_reserva', val, t('admin_settings_margen_despues_reserva_desc'))}
                      onMouseEnter={() => setShowTooltipMargenDespues(true)}
                      onMouseLeave={() => setShowTooltipMargenDespues(false)}
                    >
                      <SliderMark value={0} mt="2" ml="-2" fontSize="sm">
                        0
                      </SliderMark>
                      <SliderMark value={60} mt="2" ml="-2" fontSize="sm">
                        60
                      </SliderMark>
                      <SliderMark value={120} mt="2" ml="-2" fontSize="sm">
                        120
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="green.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="green.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltipMargenDespues}
                        label={`${margenSinDisponibilidadDespuesDeReserva} min`}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                    {margenSinDisponibilidadDespuesDeReserva} min
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_margen_despues_reserva_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Margen sin disponibilidad antes de reserva */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_margen_antes_reserva')} <Badge colorScheme="green" ml={2}>margen_sin_disponibilidad_antes_de_reserva</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="margen-sin-disponibilidad-antes"
                      min={0}
                      max={120}
                      step={1}
                      value={margenSinDisponibilidadAntesDeReserva}
                      onChange={(val) => setMargenSinDisponibilidadAntesDeReserva(val)}
                      onChangeEnd={(val) => autoSaveSetting('margen_sin_disponibilidad_antes_de_reserva', val, t('admin_settings_margen_antes_reserva_desc'))}
                      onMouseEnter={() => setShowTooltipMargenAntes(true)}
                      onMouseLeave={() => setShowTooltipMargenAntes(false)}
                    >
                      <SliderMark value={0} mt="2" ml="-2" fontSize="sm">
                        0
                      </SliderMark>
                      <SliderMark value={60} mt="2" ml="-2" fontSize="sm">
                        60
                      </SliderMark>
                      <SliderMark value={120} mt="2" ml="-2" fontSize="sm">
                        120
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="green.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="green.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltipMargenAntes}
                        label={`${margenSinDisponibilidadAntesDeReserva} min`}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                    {margenSinDisponibilidadAntesDeReserva} min
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_margen_antes_reserva_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />
              
              {/* Configuración 1: Aviso de servicios sin cubrir */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_aviso_servicios')} <Badge colorScheme="blue" ml={2}>aviso_servicios_sin_cubrir</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="aviso-servicios-sin-cubrir"
                      min={240}
                      max={360}
                      step={30}
                      value={avisoServiciosSinCubrir}
                      onChange={(val) => setAvisoServiciosSinCubrir(val)}
                      onChangeEnd={(val) => autoSaveSetting('aviso_servicios_sin_cubrir', val, t('admin_settings_aviso_servicios_desc'))}
                      onMouseEnter={() => setShowTooltip1(true)}
                      onMouseLeave={() => setShowTooltip1(false)}
                    >
                      <SliderMark value={240} mt="2" ml="-2" fontSize="sm">
                        4h
                      </SliderMark>
                      <SliderMark value={300} mt="2" ml="-2" fontSize="sm">
                        5h
                      </SliderMark>
                      <SliderMark value={360} mt="2" ml="-2" fontSize="sm">
                        6h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="blue.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="blue.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip1}
                        label={formatMinutes(avisoServiciosSinCubrir)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="blue.600">
                    {formatMinutes(avisoServiciosSinCubrir)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_aviso_servicios_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 2: Periodo de cancelación expirado */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_periodo_cancelacion')} <Badge colorScheme="orange" ml={2}>periodo_cancelacion_expirado</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="periodo-cancelacion-expirado"
                      min={120}
                      max={1440}
                      step={30}
                      value={periodoCancelacionExpirado}
                      onChange={(val) => setPeriodoCancelacionExpirado(val)}
                      onChangeEnd={(val) => autoSaveSetting('periodo_cancelacion_expirado', val, t('admin_settings_periodo_cancelacion_desc'))}
                      onMouseEnter={() => setShowTooltip2(true)}
                      onMouseLeave={() => setShowTooltip2(false)}
                    >
                      <SliderMark value={120} mt="2" ml="-2" fontSize="sm">
                        2h
                      </SliderMark>
                      <SliderMark value={720} mt="2" ml="-2" fontSize="sm">
                        12h
                      </SliderMark>
                      <SliderMark value={1440} mt="2" ml="-2" fontSize="sm">
                        24h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="orange.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="orange.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip2}
                        label={formatMinutes(periodoCancelacionExpirado)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="orange.600">
                    {formatMinutes(periodoCancelacionExpirado)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_periodo_cancelacion_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 3: Recordatorio de inicio de servicio */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_recordatorio_inicio')} <Badge colorScheme="green" ml={2}>recordatorio_inicio_servicio</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="recordatorio-inicio-servicio"
                      min={30}
                      max={120}
                      step={30}
                      value={recordatorioInicioServicio}
                      onChange={(val) => setRecordatorioInicioServicio(val)}
                      onChangeEnd={(val) => autoSaveSetting('recordatorio_inicio_servicio', val, t('admin_settings_recordatorio_inicio_desc'))}
                      onMouseEnter={() => setShowTooltip3(true)}
                      onMouseLeave={() => setShowTooltip3(false)}
                    >
                      <SliderMark value={30} mt="2" ml="-2" fontSize="sm">
                        30min
                      </SliderMark>
                      <SliderMark value={60} mt="2" ml="-2" fontSize="sm">
                        1h
                      </SliderMark>
                      <SliderMark value={90} mt="2" ml="-2" fontSize="sm">
                        1.5h
                      </SliderMark>
                      <SliderMark value={120} mt="2" ml="-2" fontSize="sm">
                        2h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="green.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="green.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip3}
                        label={formatMinutes(recordatorioInicioServicio)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="green.600">
                    {formatMinutes(recordatorioInicioServicio)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_recordatorio_inicio_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 4: Cambio a completado sin confirmar */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  Cambio a completado sin confirmar <Badge colorScheme="cyan" ml={2}>cambio_a_completado_sin_confirmar</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="cambio-completado-sin-confirmar"
                      min={0}
                      max={120}
                      step={10}
                      value={cambioACompletadoSinConfirmar}
                      onChange={(val) => setCambioACompletadoSinConfirmar(val)}
                      onChangeEnd={(val) => autoSaveSetting('cambio_a_completado_sin_confirmar', val, "Momento posterior al fin del servicio, en el que una reserva pasará automáticamente de 'en curso' a 'completado sin confirmar'")}
                      onMouseEnter={() => setShowTooltip4(true)}
                      onMouseLeave={() => setShowTooltip4(false)}
                    >
                      <SliderMark value={0} mt="2" ml="-1" fontSize="sm">
                        0h
                      </SliderMark>
                      <SliderMark value={60} mt="2" ml="-2" fontSize="sm">
                        1h
                      </SliderMark>
                      <SliderMark value={120} mt="2" ml="-2" fontSize="sm">
                        2h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="cyan.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="cyan.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip4}
                        label={formatMinutes(cambioACompletadoSinConfirmar)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="cyan.600">
                    {formatMinutes(cambioACompletadoSinConfirmar)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  Momento posterior al fin del servicio, en el que una reserva pasará automáticamente de 'en curso' a 'completado sin confirmar'
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 5: Recordatorio de devolución de servicio */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_recordatorio_devolucion')} <Badge colorScheme="purple" ml={2}>recordatorio_devolucion_servicio</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="recordatorio-devolucion-servicio"
                      min={60}
                      max={180}
                      step={30}
                      value={recordatorioDevolucionServicio}
                      onChange={(val) => setRecordatorioDevolucionServicio(val)}
                      onChangeEnd={(val) => autoSaveSetting('recordatorio_devolucion_servicio', val, t('admin_settings_recordatorio_devolucion_desc'))}
                      onMouseEnter={() => setShowTooltip5(true)}
                      onMouseLeave={() => setShowTooltip5(false)}
                    >
                      <SliderMark value={60} mt="2" ml="-2" fontSize="sm">
                        1h
                      </SliderMark>
                      <SliderMark value={120} mt="2" ml="-2" fontSize="sm">
                        2h
                      </SliderMark>
                      <SliderMark value={180} mt="2" ml="-2" fontSize="sm">
                        3h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="purple.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="purple.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip5}
                        label={formatMinutes(recordatorioDevolucionServicio)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="purple.600">
                    {formatMinutes(recordatorioDevolucionServicio)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_recordatorio_devolucion_desc')}
                </FormHelperText>
              </FormControl>

            </VStack>
            )}
          </CardBody>
          </Collapse>
        </Card>

        {/* Sección: Datos de contacto */}
        <Card bg="teal.50" variant="outline" borderColor="gray.700">
          <CardHeader 
            cursor="pointer" 
            onClick={() => handleSectionToggle('contacto', isContactoOpen, setIsContactoOpen)}
            _hover={{ bg: 'teal.100' }}
            borderTopRadius="md"
            borderBottomRadius={isContactoOpen ? "none" : "md"}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">{t('admin_settings_seccion_contacto')}</Heading>
              <Text fontSize="2xl">{isContactoOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isContactoOpen} animateOpacity>
            <CardBody>
              {sectionLoading.contacto ? (
                <Text fontWeight="bold" color="gray.600">Cargando...</Text>
              ) : (
              <VStack spacing={8} align="stretch">
                
                {/* Configuración 7: Email del desarrollador */}
                <FormControl isInvalid={!!emailError}>
                  <FormLabel fontWeight="bold">
                    {t('admin_settings_developer_email')} <Badge colorScheme="teal" ml={2}>developer_email</Badge>
                  </FormLabel>
                  <Input
                    type="email"
                    value={developerEmail}
                    onChange={(e) => setDeveloperEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="admin@overtaxi.es"
                    bg="white"
                    color="gray.800"
                    _placeholder={{ color: 'gray.400' }}
                  />
                  {emailError && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {emailError}
                    </Text>
                  )}
                  <FormHelperText mt={4}>
                    {t('admin_settings_developer_email_desc')}
                  </FormHelperText>
                </FormControl>

                {/* Configuración: Email del cliente anónimo */}
                <FormControl isInvalid={!!anonymousEmailError}>
                  <FormLabel fontWeight="bold">
                    {t('admin_settings_anonymous_customer_email')} <Badge colorScheme="teal" ml={2}>anonymous_customer_email</Badge>
                  </FormLabel>
                  <Input
                    type="email"
                    value={anonymousCustomerEmail}
                    onChange={(e) => setAnonymousCustomerEmail(e.target.value)}
                    onBlur={handleAnonymousEmailBlur}
                    placeholder="htabletcasa@gmail.com"
                    bg="white"
                    color="gray.800"
                    _placeholder={{ color: 'gray.400' }}
                  />
                  {anonymousEmailError && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {anonymousEmailError}
                    </Text>
                  )}
                  <FormHelperText mt={4}>
                    {t('admin_settings_anonymous_customer_email_desc')}
                  </FormHelperText>
                </FormControl>

              </VStack>
              )}
            </CardBody>
          </Collapse>
        </Card>

        {/* Sección: Redsys */}
        <Card bg="purple.50" variant="outline" borderColor="gray.700">
          <CardHeader 
            cursor="pointer" 
            onClick={() => handleSectionToggle('redsys', isRedsysOpen, setIsRedsysOpen)}
            _hover={{ bg: 'purple.100' }}
            borderTopRadius="md"
            borderBottomRadius={isRedsysOpen ? "none" : "md"}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">
                {t('admin_settings_seccion_redsys')} ({t('admin_settings_env_label')} {isProductionApi ? t('admin_settings_env_prod') : t('admin_settings_env_dev')})
              </Heading>
              <Text fontSize="2xl">{isRedsysOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isRedsysOpen} animateOpacity>
            <CardBody>
            {sectionLoading.redsys ? (
              <Text fontWeight="bold" color="gray.600">Cargando...</Text>
            ) : (
            <VStack spacing={8} align="stretch">
              
              {/* Configuración 10: Código de comercio Redsys */}
              <FormControl isInvalid={!!merchantCodeError}>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_merchant_code')} <Badge colorScheme="purple" ml={2}>redsys_merchant_code</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysMerchantCode}
                  onChange={(e) => setRedsysMerchantCode(e.target.value)}
                  onBlur={handleMerchantCodeBlur}
                  placeholder="367589983"
                  maxLength={9}
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                {merchantCodeError && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {merchantCodeError}
                  </Text>
                )}
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_merchant_code_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 11: Terminal Redsys */}
              <FormControl isInvalid={!!terminalError}>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_terminal')} <Badge colorScheme="purple" ml={2}>redsys_terminal</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysTerminal}
                  onChange={(e) => setRedsysTerminal(e.target.value)}
                  onBlur={handleTerminalBlur}
                  placeholder="1"
                  maxLength={2}
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                {terminalError && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {terminalError}
                  </Text>
                )}
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_terminal_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 12: Moneda Redsys */}
              <FormControl isInvalid={!!currencyError}>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_currency')} <Badge colorScheme="purple" ml={2}>redsys_currency</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysCurrency}
                  onChange={(e) => setRedsysCurrency(e.target.value)}
                  onBlur={handleCurrencyBlur}
                  placeholder="978"
                  maxLength={3}
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                {currencyError && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {currencyError}
                  </Text>
                )}
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_currency_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 13: Tipo de transacción Redsys */}
              <FormControl isInvalid={!!transactionTypeError}>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_transaction_type')} <Badge colorScheme="purple" ml={2}>redsys_transaction_type</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysTransactionType}
                  onChange={(e) => setRedsysTransactionType(e.target.value)}
                  onBlur={handleTransactionTypeBlur}
                  placeholder="0"
                  maxLength={1}
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                {transactionTypeError && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {transactionTypeError}
                  </Text>
                )}
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_transaction_type_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 14: Número móvil Bizum Redsys */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_bizum_mobilenumber')} <Badge colorScheme="purple" ml={2}>redsys_bizum_mobilenumber</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysBizumMobilenumber}
                  onChange={(e) => setRedsysBizumMobilenumber(e.target.value)}
                  onBlur={() => autoSaveStringSetting('redsys_bizum_mobilenumber', redsysBizumMobilenumber, t('admin_settings_redsys_bizum_mobilenumber_desc'))}
                  placeholder="+34700000000"
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_bizum_mobilenumber_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 15: URL de producción Redsys */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_production_url')} <Badge colorScheme="purple" ml={2}>redsys_prod_url</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysProductionUrl}
                  onChange={(e) => setRedsysProductionUrl(e.target.value)}
                  onBlur={() => autoSaveStringSetting('redsys_prod_url', redsysProductionUrl, t('admin_settings_redsys_production_url_desc'))}
                  placeholder="https://sis.redsys.es:25443/sis/rest/iniciaPeticionREST"
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_production_url_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 16: URL de formulario Redsys */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_form_url')} <Badge colorScheme="purple" ml={2}>redsys_prod_form_url</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysFormUrl}
                  onChange={(e) => setRedsysFormUrl(e.target.value)}
                  onBlur={() => autoSaveStringSetting('redsys_prod_form_url', redsysFormUrl, t('admin_settings_redsys_form_url_desc'))}
                  placeholder="https://sis.redsys.es/sis/realizarPago"
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_form_url_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 17: URL de notificación Redsys */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_redsys_merchanturl')} <Badge colorScheme="purple" ml={2}>redsys_prod_merchanturl</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysMerchantUrl}
                  onChange={(e) => setRedsysMerchantUrl(e.target.value)}
                  onBlur={() => autoSaveStringSetting('redsys_prod_merchanturl', redsysMerchantUrl, t('admin_settings_redsys_merchanturl_desc'))}
                  placeholder="https://www.overtaxi.es/api/pagos/notificacion/"
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                />
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_merchanturl_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 17: URL de éxito Redsys */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_redsys_urlok')} <Badge colorScheme="purple" ml={2}>redsys_prod_urlok</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysUrlOk}
                  onChange={(e) => setRedsysUrlOk(e.target.value)}
                  onBlur={() => autoSaveStringSetting('redsys_prod_urlok', redsysUrlOk, t('admin_settings_redsys_urlok_desc'))}
                  placeholder="https://www.overtaxi.es/cliente/reserva?estado=ok&reserva_id="
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                  className="admin-form-input"
                />
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_urlok_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 18: URL de error Redsys */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_redsys_urlko')} <Badge colorScheme="purple" ml={2}>redsys_prod_urlko</Badge>
                </FormLabel>
                <Input
                  type="text"
                  value={redsysUrlKo}
                  onChange={(e) => setRedsysUrlKo(e.target.value)}
                  onBlur={() => autoSaveStringSetting('redsys_prod_urlko', redsysUrlKo, t('admin_settings_redsys_urlko_desc'))}
                  placeholder="https://www.overtaxi.es/cliente/reserva?estado=ko&reserva_id="
                  bg="white"
                  color="gray.800"
                  _placeholder={{ color: 'gray.400' }}
                  className="admin-form-input"
                />
                <FormHelperText mt={4}>
                  {t('admin_settings_redsys_urlko_desc')}
                </FormHelperText>
              </FormControl>

            </VStack>
            )}
          </CardBody>
          </Collapse>
        </Card>

        {/* Sección: Seguridad y autenticación */}
        <Card bg="red.50" variant="outline" borderColor="gray.700">
          <CardHeader 
            cursor="pointer" 
            onClick={() => handleSectionToggle('seguridad', isSeguridadOpen, setIsSeguridadOpen)}
            _hover={{ bg: 'red.100' }}
            borderTopRadius="md"
            borderBottomRadius={isSeguridadOpen ? "none" : "md"}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">{t('admin_settings_seccion_seguridad')}</Heading>
              <Text fontSize="2xl">{isSeguridadOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isSeguridadOpen} animateOpacity>
            <CardBody>
            {sectionLoading.seguridad ? (
              <Text fontWeight="bold" color="gray.600">Cargando...</Text>
            ) : (
            <VStack spacing={8} align="stretch">
              
              {/* Configuración 5: Duración de tokens de acceso */}
              <FormControl>
                <FormLabel fontWeight="bold">
                  {t('admin_settings_token_expire')} <Badge colorScheme="red" ml={2}>access_token_expire_minutes</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="access-token-expire-minutes"
                      min={60}
                      max={1440}
                      step={60}
                      value={accessTokenExpireMinutes}
                      onChange={(val) => setAccessTokenExpireMinutes(val)}
                      onChangeEnd={(val) => autoSaveSetting('access_token_expire_minutes', val, t('admin_settings_token_expire_desc'))}
                      onMouseEnter={() => setShowTooltip5(true)}
                      onMouseLeave={() => setShowTooltip5(false)}
                    >
                      <SliderMark value={60} mt="2" ml="-2" fontSize="sm">
                        1h
                      </SliderMark>
                      <SliderMark value={360} mt="2" ml="-2" fontSize="sm">
                        6h
                      </SliderMark>
                      <SliderMark value={720} mt="2" ml="-2" fontSize="sm">
                        12h
                      </SliderMark>
                      <SliderMark value={1440} mt="2" ml="-2" fontSize="sm">
                        24h
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="red.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="red.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltip5}
                        label={formatMinutes(accessTokenExpireMinutes)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="red.600">
                    {formatMinutes(accessTokenExpireMinutes)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_token_expire_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              {/* Configuración 6: Duración de sesiones */}
              <FormControl>
                <FormLabel fontWeight="bold" className="admin-form-label">
                  {t('admin_settings_session_expire')} <Badge colorScheme="orange" ml={2}>session_expiration_minutes</Badge>
                </FormLabel>
                <HStack spacing={4} align="center">
                  <Box flex="1">
                    <Slider
                      aria-label="session-expiration-minutes"
                      min={720}
                      max={7200}
                      step={360}
                      value={sessionExpirationMinutes}
                      onChange={(val) => setSessionExpirationMinutes(val)}
                      onChangeEnd={(val) => autoSaveSetting('session_expiration_minutes', val, t('admin_settings_session_expire_desc'))}
                      onMouseEnter={() => setShowTooltipSession(true)}
                      onMouseLeave={() => setShowTooltipSession(false)}
                    >
                      <SliderMark value={720} mt="2" ml="-2" fontSize="sm">
                        12h
                      </SliderMark>
                      <SliderMark value={1440} mt="2" ml="-2" fontSize="sm">
                        1d
                      </SliderMark>
                      <SliderMark value={2880} mt="2" ml="-2" fontSize="sm">
                        2d
                      </SliderMark>
                      <SliderMark value={7200} mt="2" ml="-2" fontSize="sm">
                        5d
                      </SliderMark>
                      <SliderTrack>
                        <SliderFilledTrack bg="orange.500" />
                      </SliderTrack>
                      <Tooltip
                        hasArrow
                        bg="orange.500"
                        color="white"
                        placement="top"
                        isOpen={showTooltipSession}
                        label={formatMinutes(sessionExpirationMinutes)}
                      >
                        <SliderThumb boxSize={6} />
                      </Tooltip>
                    </Slider>
                  </Box>
                  <Text minW="80px" textAlign="right" fontWeight="bold" color="orange.600">
                    {formatMinutes(sessionExpirationMinutes)}
                  </Text>
                </HStack>
                <FormHelperText mt={4}>
                  {t('admin_settings_session_expire_desc')}
                </FormHelperText>
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_permitir_login_tiktok_label')} <Badge colorScheme="teal" ml={2}>permitir_login_tiktok</Badge>
                  </FormLabel>
                  <FormHelperText mt={1}>
                    {t('admin_settings_permitir_login_tiktok_desc')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={permitirLoginTiktok}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setPermitirLoginTiktok(value);
                    autoSaveBoolSetting('permitir_login_tiktok', value, t('admin_settings_permitir_login_tiktok_label'));
                  }}
                />
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_permitir_login_google_label')} <Badge colorScheme="teal" ml={2}>permitir_login_google</Badge>
                  </FormLabel>
                  <FormHelperText mt={1}>
                    {t('admin_settings_permitir_login_google_desc')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={permitirLoginGoogle}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setPermitirLoginGoogle(value);
                    autoSaveBoolSetting('permitir_login_google', value, t('admin_settings_permitir_login_google_label'));
                  }}
                />
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_permitir_login_instagram_label')} <Badge colorScheme="teal" ml={2}>permitir_login_instagram</Badge>
                  </FormLabel>
                  <FormHelperText mt={1}>
                    {t('admin_settings_permitir_login_instagram_desc')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={permitirLoginInstagram}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setPermitirLoginInstagram(value);
                    autoSaveBoolSetting('permitir_login_instagram', value, t('admin_settings_permitir_login_instagram_label'));
                  }}
                />
              </FormControl>

              <Divider />

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_permitir_login_facebook_label')} <Badge colorScheme="teal" ml={2}>permitir_login_facebook</Badge>
                  </FormLabel>
                  <FormHelperText mt={1}>
                    {t('admin_settings_permitir_login_facebook_desc')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={permitirLoginFacebook}
                  onChange={(e) => {
                    const value = e.target.checked;
                    setPermitirLoginFacebook(value);
                    autoSaveBoolSetting('permitir_login_facebook', value, t('admin_settings_permitir_login_facebook_label'));
                  }}
                />
              </FormControl>

            </VStack>
            )}
          </CardBody>
          </Collapse>
        </Card>

        <Card bg="teal.50" variant="outline" borderColor="gray.700">
          <CardHeader
            cursor="pointer"
            onClick={() => handleSectionToggle('vuelos', isVuelosOpen, setIsVuelosOpen)}
            _hover={{ bg: 'teal.100' }}
            borderTopRadius="md"
            borderBottomRadius={isVuelosOpen ? 'none' : 'md'}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">
                {t('admin_settings_vuelos_title', { defaultValue: 'Vuelos' })}
              </Heading>
              <Text fontSize="2xl">{isVuelosOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isVuelosOpen} animateOpacity>
            <CardBody>
              {sectionLoading.vuelos ? (
                <Text fontWeight="bold" color="gray.600">Cargando...</Text>
              ) : (
                <VStack spacing={6} align="stretch">
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <FormLabel mb={0} fontWeight="bold">
                        {t('admin_settings_habilitar_flightapi_label', { defaultValue: 'Habilitar FlightAPI' })} <Badge colorScheme="teal" ml={2}>habilitar_flightapi</Badge>
                      </FormLabel>
                      <FormHelperText mt={1}>
                        {t('admin_settings_habilitar_flightapi_desc', { defaultValue: 'Activa o desactiva la conexión con FlightAPI para consultar estado de vuelos.' })}
                      </FormHelperText>
                    </Box>
                    <Switch
                      isChecked={habilitarFlightapi}
                      onChange={(e) => {
                        const value = e.target.checked;
                        setHabilitarFlightapi(value);
                        autoSaveBoolSetting('habilitar_flightapi', value, t('admin_settings_habilitar_flightapi_label', { defaultValue: 'Habilitar FlightAPI' }));
                      }}
                    />
                  </FormControl>

                  <Divider />

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <FormLabel mb={0} fontWeight="bold">
                        {t('admin_settings_habilitar_aeroapi_label', { defaultValue: 'Habilitar AeroAPI' })} <Badge colorScheme="teal" ml={2}>habilitar_aeroapi</Badge>
                      </FormLabel>
                      <FormHelperText mt={1}>
                        {t('admin_settings_habilitar_aeroapi_desc', { defaultValue: 'Activa o desactiva la conexión con AeroAPI para consultar estado de vuelos.' })}
                      </FormHelperText>
                    </Box>
                    <Switch
                      isChecked={habilitarAeroapi}
                      onChange={(e) => {
                        const value = e.target.checked;
                        setHabilitarAeroapi(value);
                        autoSaveBoolSetting('habilitar_aeroapi', value, t('admin_settings_habilitar_aeroapi_label', { defaultValue: 'Habilitar AeroAPI' }));
                      }}
                    />
                  </FormControl>

                  <Divider />

                  <FormControl>
                    <FormLabel mb={0} fontWeight="bold">
                      {t('admin_settings_flightapi_timeout_seconds_label', { defaultValue: 'Timeout FlightAPI (segundos)' })} <Badge colorScheme="teal" ml={2}>flightapi_timeout_seconds</Badge>
                    </FormLabel>
                    <HStack spacing={4} align="center">
                      <Box flex="1">
                        <Slider
                          min={5}
                          max={30}
                          step={1}
                          value={flightapiTimeoutSeconds}
                          onChange={(value) => setFlightapiTimeoutSeconds(value)}
                          onChangeEnd={(value) => autoSaveSetting('flightapi_timeout_seconds', value, t('admin_settings_flightapi_timeout_seconds_label', { defaultValue: 'Timeout FlightAPI (segundos)' }))}
                          isDisabled={isLoading}
                        >
                          <SliderMark value={5} mt={2} fontSize="sm">5s</SliderMark>
                          <SliderMark value={10} mt={2} fontSize="sm">10s</SliderMark>
                          <SliderMark value={20} mt={2} fontSize="sm">20s</SliderMark>
                          <SliderMark value={30} mt={2} ml={-2} fontSize="sm">30s</SliderMark>
                          <SliderTrack bg="teal.100">
                            <SliderFilledTrack bg="teal.500" />
                          </SliderTrack>
                          <SliderThumb />
                        </Slider>
                      </Box>
                      <Text minW="56px" fontWeight="bold" color="teal.700">
                        {flightapiTimeoutSeconds}s
                      </Text>
                    </HStack>
                    <FormHelperText mt={1}>
                      {t('admin_settings_flightapi_timeout_seconds_desc', { defaultValue: 'Tiempo máximo de espera para validar conectividad con FlightAPI.' })}
                    </FormHelperText>
                  </FormControl>
                </VStack>
              )}
            </CardBody>
          </Collapse>
        </Card>

        {/* Sección: Chat de soporte */}
        <Card bg="teal.50" variant="outline" borderColor="gray.700">
          <CardHeader
            cursor="pointer"
            onClick={() => handleSectionToggle('chatSoporte', isChatSoporteOpen, setIsChatSoporteOpen)}
            _hover={{ bg: 'teal.100' }}
            borderTopRadius="md"
            borderBottomRadius={isChatSoporteOpen ? "none" : "md"}
          >
            <HStack justify="space-between">
              <Heading size="md" color="black">
                {t('admin_settings_chat_support_title')} ({t('admin_settings_env_label')} {isProductionApi ? t('admin_settings_env_prod') : t('admin_settings_env_dev')})
              </Heading>
              <Text fontSize="2xl">{isChatSoporteOpen ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          <Collapse in={isChatSoporteOpen} animateOpacity>
            <CardBody>
              {sectionLoading.chatSoporte ? (
                <Text fontWeight="bold" color="gray.600">Cargando...</Text>
              ) : (
              <VStack spacing={6} align="stretch">
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <FormLabel mb={0} fontWeight="bold">
                      {t('admin_settings_chat_support_whatsapp_channel_label', { defaultValue: 'Habilitar canal Whatsapp' })} <Badge colorScheme="teal" ml={2}>chat_support_whatsapp_enabled</Badge>
                    </FormLabel>
                    <FormHelperText mt={1}>
                      {t('admin_settings_chat_support_whatsapp_channel_desc', { defaultValue: 'Activa o desactiva el canal de soporte por WhatsApp.' })}
                    </FormHelperText>
                  </Box>
                  <Switch
                    isChecked={chatSupportWhatsappEnabled}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setChatSupportWhatsappEnabled(value);
                      autoSaveChatSetting('chat_support_whatsapp_enabled', value, t('admin_settings_chat_support_whatsapp_channel_label', { defaultValue: 'Habilitar canal Whatsapp' }));
                    }}
                  />
                </FormControl>

                <Divider />

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <FormLabel mb={0} fontWeight="bold">
                      {t('admin_settings_chat_support_telegram_channel_label', { defaultValue: 'Habilitar canal Telegram' })} <Badge colorScheme="teal" ml={2}>chat_support_telegram_enabled</Badge>
                    </FormLabel>
                    <FormHelperText mt={1}>
                      {t('admin_settings_chat_support_telegram_channel_desc', { defaultValue: 'Activa o desactiva el canal de soporte por Telegram.' })}
                    </FormHelperText>
                  </Box>
                  <Switch
                    isChecked={chatSupportTelegramEnabled}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setChatSupportTelegramEnabled(value);
                      autoSaveChatSetting('chat_support_telegram_enabled', value, t('admin_settings_chat_support_telegram_channel_label', { defaultValue: 'Habilitar canal Telegram' }));
                    }}
                  />
                </FormControl>

                <Divider />

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <FormLabel mb={0} fontWeight="bold">
                      {t('admin_settings_chat_support_toggle_label')} <Badge colorScheme="teal" ml={2}>chat_support_enabled</Badge>
                    </FormLabel>
                    <FormHelperText mt={1}>
                      {t('admin_settings_chat_support_toggle_desc')}
                    </FormHelperText>
                  </Box>
                  <Switch
                    isChecked={chatSupportEnabled}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setChatSupportEnabled(value);
                      autoSaveChatSetting('chat_support_enabled', value, t('admin_settings_chat_support_toggle_label'));
                    }}
                  />
                </FormControl>

                <Divider />

                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <FormLabel mb={0} fontWeight="bold">
                      {t('admin_settings_chat_support_publicly_available_label', { defaultValue: 'Chat disponible públicamente' })} <Badge colorScheme="teal" ml={2}>chat_support_publicly_available</Badge>
                    </FormLabel>
                    <FormHelperText mt={1}>
                      {t('admin_settings_chat_support_publicly_available_desc', { defaultValue: 'Si está desactivado, el chat no se mostrará a usuarios públicos.' })}
                    </FormHelperText>
                  </Box>
                  <Switch
                    isChecked={chatSupportPubliclyAvailable}
                    onChange={(e) => {
                      const value = e.target.checked;
                      setChatSupportPubliclyAvailable(value);
                      autoSaveChatSetting('chat_support_publicly_available', value, t('admin_settings_chat_support_publicly_available_label', { defaultValue: 'Chat disponible públicamente' }));
                    }}
                  />
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_poll_interval_label', { defaultValue: 'Frecuencia de polling del chat (segundos)' })} <Badge colorScheme="teal" ml={2}>chat_support_poll_interval_seconds</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={8}
                        max={40}
                        step={1}
                        value={chatSupportPollIntervalSeconds}
                        onChange={(value) => setChatSupportPollIntervalSeconds(value)}
                        onChangeEnd={(value) => autoSaveChatIntSetting('chat_support_poll_interval_seconds', value, t('admin_settings_chat_support_poll_interval_label', { defaultValue: 'Frecuencia de polling del chat (segundos)' }))}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={8} mt={2} fontSize="sm">8s</SliderMark>
                        <SliderMark value={10} mt={2} fontSize="sm">10s</SliderMark>
                        <SliderMark value={20} mt={2} fontSize="sm">20s</SliderMark>
                        <SliderMark value={30} mt={2} fontSize="sm">30s</SliderMark>
                        <SliderMark value={40} mt={2} ml={-2} fontSize="sm">40s</SliderMark>
                        <SliderTrack bg="teal.100">
                          <SliderFilledTrack bg="teal.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text minW="56px" fontWeight="bold" color="teal.700">
                      {chatSupportPollIntervalSeconds}s
                    </Text>
                  </HStack>
                  <FormHelperText mt={1}>
                    {t('admin_settings_chat_support_poll_interval_desc', { defaultValue: 'Define cada cuántos segundos se consulta el historial cuando el WebSocket no está activo. Valor por defecto: 10s.' })}
                  </FormHelperText>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_settings_refresh_interval_label', { defaultValue: 'Frecuencia de refresco de configuración del chat (segundos)' })} <Badge colorScheme="teal" ml={2}>chat_support_settings_refresh_interval_seconds</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={8}
                        max={60}
                        step={1}
                        value={chatSupportSettingsRefreshIntervalSeconds}
                        onChange={(value) => setChatSupportSettingsRefreshIntervalSeconds(value)}
                        onChangeEnd={(value) => autoSaveChatIntSetting('chat_support_settings_refresh_interval_seconds', value, t('admin_settings_chat_support_settings_refresh_interval_label', { defaultValue: 'Frecuencia de refresco de configuración del chat (segundos)' }))}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={8} mt={2} fontSize="sm">8s</SliderMark>
                        <SliderMark value={30} mt={2} fontSize="sm">30s</SliderMark>
                        <SliderMark value={45} mt={2} fontSize="sm">45s</SliderMark>
                        <SliderMark value={60} mt={2} ml={-2} fontSize="sm">60s</SliderMark>
                        <SliderTrack bg="teal.100">
                          <SliderFilledTrack bg="teal.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text minW="56px" fontWeight="bold" color="teal.700">
                      {chatSupportSettingsRefreshIntervalSeconds}s
                    </Text>
                  </HStack>
                  <FormHelperText mt={1}>
                    {t('admin_settings_chat_support_settings_refresh_interval_desc', { defaultValue: 'Define cada cuántos segundos se refresca la configuración pública del chat. Valor por defecto: 30s.' })}
                  </FormHelperText>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_admin_messages_poll_interval_label', { defaultValue: 'Frecuencia de polling de mensajes en Admin Chat (segundos)' })} <Badge colorScheme="teal" ml={2}>chat_support_admin_messages_poll_interval_seconds</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={8}
                        max={30}
                        step={1}
                        value={chatSupportAdminMessagesPollIntervalSeconds}
                        onChange={(value) => setChatSupportAdminMessagesPollIntervalSeconds(value)}
                        onChangeEnd={(value) => autoSaveChatIntSetting('chat_support_admin_messages_poll_interval_seconds', value, t('admin_settings_chat_support_admin_messages_poll_interval_label', { defaultValue: 'Frecuencia de polling de mensajes en Admin Chat (segundos)' }))}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={8} mt={2} fontSize="sm">8s</SliderMark>
                        <SliderMark value={12} mt={2} fontSize="sm">12s</SliderMark>
                        <SliderMark value={20} mt={2} fontSize="sm">20s</SliderMark>
                        <SliderMark value={30} mt={2} ml={-2} fontSize="sm">30s</SliderMark>
                        <SliderTrack bg="teal.100">
                          <SliderFilledTrack bg="teal.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text minW="56px" fontWeight="bold" color="teal.700">
                      {chatSupportAdminMessagesPollIntervalSeconds}s
                    </Text>
                  </HStack>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_admin_channels_poll_interval_label', { defaultValue: 'Frecuencia de polling de canales en Admin Chat (segundos)' })} <Badge colorScheme="teal" ml={2}>chat_support_admin_channels_poll_interval_seconds</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={15}
                        max={60}
                        step={1}
                        value={chatSupportAdminChannelsPollIntervalSeconds}
                        onChange={(value) => setChatSupportAdminChannelsPollIntervalSeconds(value)}
                        onChangeEnd={(value) => autoSaveChatIntSetting('chat_support_admin_channels_poll_interval_seconds', value, t('admin_settings_chat_support_admin_channels_poll_interval_label', { defaultValue: 'Frecuencia de polling de canales en Admin Chat (segundos)' }))}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={15} mt={2} fontSize="sm">15s</SliderMark>
                        <SliderMark value={30} mt={2} fontSize="sm">30s</SliderMark>
                        <SliderMark value={45} mt={2} fontSize="sm">45s</SliderMark>
                        <SliderMark value={60} mt={2} ml={-2} fontSize="sm">60s</SliderMark>
                        <SliderTrack bg="teal.100">
                          <SliderFilledTrack bg="teal.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text minW="56px" fontWeight="bold" color="teal.700">
                      {chatSupportAdminChannelsPollIntervalSeconds}s
                    </Text>
                  </HStack>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_admin_logs_poll_interval_label', { defaultValue: 'Frecuencia de refresco de logs en Admin Chat (segundos)' })} <Badge colorScheme="teal" ml={2}>chat_support_admin_logs_poll_interval_seconds</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={30}
                        max={180}
                        step={5}
                        value={chatSupportAdminLogsPollIntervalSeconds}
                        onChange={(value) => setChatSupportAdminLogsPollIntervalSeconds(value)}
                        onChangeEnd={(value) => autoSaveChatIntSetting('chat_support_admin_logs_poll_interval_seconds', value, t('admin_settings_chat_support_admin_logs_poll_interval_label', { defaultValue: 'Frecuencia de refresco de logs en Admin Chat (segundos)' }))}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={30} mt={2} fontSize="sm">30s</SliderMark>
                        <SliderMark value={60} mt={2} fontSize="sm">60s</SliderMark>
                        <SliderMark value={120} mt={2} fontSize="sm">120s</SliderMark>
                        <SliderMark value={180} mt={2} ml={-4} fontSize="sm">180s</SliderMark>
                        <SliderTrack bg="teal.100">
                          <SliderFilledTrack bg="teal.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text minW="64px" fontWeight="bold" color="teal.700">
                      {chatSupportAdminLogsPollIntervalSeconds}s
                    </Text>
                  </HStack>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_admin_telegram_status_poll_interval_label', { defaultValue: 'Frecuencia de verificación Telegram en Admin Chat (segundos)' })} <Badge colorScheme="teal" ml={2}>chat_support_admin_telegram_status_poll_interval_seconds</Badge>
                  </FormLabel>
                  <HStack spacing={4} align="center">
                    <Box flex="1">
                      <Slider
                        min={30}
                        max={180}
                        step={5}
                        value={chatSupportAdminTelegramStatusPollIntervalSeconds}
                        onChange={(value) => setChatSupportAdminTelegramStatusPollIntervalSeconds(value)}
                        onChangeEnd={(value) => autoSaveChatIntSetting('chat_support_admin_telegram_status_poll_interval_seconds', value, t('admin_settings_chat_support_admin_telegram_status_poll_interval_label', { defaultValue: 'Frecuencia de verificación Telegram en Admin Chat (segundos)' }))}
                        isDisabled={isLoading}
                      >
                        <SliderMark value={30} mt={2} fontSize="sm">30s</SliderMark>
                        <SliderMark value={60} mt={2} fontSize="sm">60s</SliderMark>
                        <SliderMark value={120} mt={2} fontSize="sm">120s</SliderMark>
                        <SliderMark value={180} mt={2} ml={-4} fontSize="sm">180s</SliderMark>
                        <SliderTrack bg="teal.100">
                          <SliderFilledTrack bg="teal.500" />
                        </SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>
                    <Text minW="64px" fontWeight="bold" color="teal.700">
                      {chatSupportAdminTelegramStatusPollIntervalSeconds}s
                    </Text>
                  </HStack>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_docs_eusta_label')} <Badge colorScheme="teal" ml={2}>chat_support_docs_path</Badge>
                  </FormLabel>
                  <Input
                    value={chatSupportDocsPath}
                    onChange={(e) => setChatSupportDocsPath(e.target.value)}
                    onBlur={() => autoSaveChatStringSetting('chat_support_docs_path', chatSupportDocsPath, t('admin_settings_chat_support_docs_eusta_label'))}
                    placeholder={t('admin_settings_chat_support_docs_eusta_placeholder')}
                  />
                  <FormHelperText mt={1}>
                    {t('admin_settings_chat_support_docs_eusta_help')}
                  </FormHelperText>
                </FormControl>

                <Divider />

                <FormControl>
                  <FormLabel mb={0} fontWeight="bold">
                    {t('admin_settings_chat_support_docs_chicho_label')} <Badge colorScheme="teal" ml={2}>chat_support_docs_path_chicho</Badge>
                  </FormLabel>
                  <Input
                    value={chatSupportDocsPathChicho}
                    onChange={(e) => setChatSupportDocsPathChicho(e.target.value)}
                    onBlur={() => autoSaveChatStringSetting('chat_support_docs_path_chicho', chatSupportDocsPathChicho, t('admin_settings_chat_support_docs_chicho_label'))}
                    placeholder={t('admin_settings_chat_support_docs_chicho_placeholder')}
                  />
                  <FormHelperText mt={1}>
                    {t('admin_settings_chat_support_docs_chicho_help')}
                  </FormHelperText>
                </FormControl>
              </VStack>
              )}
            </CardBody>
          </Collapse>
        </Card>

      </VStack>
    </Box>
  );
};

export default AdminSettings;
